import { OffersItemDto, ScreeningCheckStatus, ScreeningConsent } from 'common/types/services/api/offers/offer-item-dto.type';
import DocumentCheckbox from 'components/molecules/CheckboxDocument/CheckboxDocument';
import { Modal } from 'components/organisms/Modal';
import { useWindowSize } from 'hooks';
import { useAcceptBackgroundCheck } from 'hooks/query/tenant-screening/use-accept-background-check';
import { Button, RadioButton } from 'legacy-components/componets';
import PropertyInfo from './PropertyInfo';
import { EstateTypeDto, NotificationType } from 'common/enums/enums';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDeclineBackgroundCheck } from 'hooks/query/tenant-screening/use-decline-background-check';
import PayTenantBackgroundScreeningDialog from 'legacy-pages/landlord/profile/offers/components/PayTenantBackgroundScreeningDialog';
import { openNotificationModal } from 'store/use-notification-modal-store';
import { ScreeningServicePlanPayment } from 'common/types/services/api/tenant-screening';
import { useRequestBackgroundCheckPayment } from 'hooks/query/tenant-screening/useRequestBackgroundCheckPayment';

interface Props {
  onClose: () => void;
  isOpen: boolean;
  offer: OffersItemDto | null;
}

const BackgroundCheckRequestModal = ({ onClose, isOpen, offer }: Props) => {
  const { isMobile } = useWindowSize();
  const [acceptTermsConditions, setAcceptTermsAndCondition] = useState(false);
  const [openPayScreeningServiceDialog, setOpenPayScreeningServiceDialog] = useState(false);
  const [screeningPaymentContext, setScreeningPaymentContext] = useState<ScreeningServicePlanPayment | undefined>();
  const { mutateAsync: acceptBackgroundCheckRequest, isPending: isAcceptingBackgroundCheckRequest } = useAcceptBackgroundCheck();
  const { mutateAsync: declineBackgroundCheckRequest, isPending: isDecliningBackgroundCheckRequest } = useDeclineBackgroundCheck();
  const [alreadyRequestedPlans, setAlreadyRequestedPlans] = useState<ScreeningConsent[]>([]);
  const [renterToPayPlans, setRenterToPayPlans] = useState<ScreeningConsent[]>([]);
  const [landlordToPayPlans, setLandlordToPayPlans] = useState<ScreeningConsent[]>([]);
  const [totalSumForLandlord, setTotalSumForLandlord] = useState(0);
  const [totalSumForRenter, setTotalSumForRenter] = useState(0);
  
  const { mutateAsync: requestBgCheckPayment, isPending: pendingBgCheckPayment } = useRequestBackgroundCheckPayment({
    onSuccess: data => data && setScreeningPaymentContext(data),
    onError: (e) => {
      openNotificationModal({
        type: NotificationType.ERROR,
        title: 'Failed to process background check payment. Try again later.',
      });
    }
  });

  const handleScreeningPaymentSucceeded = async () => {
    const notAcceptedPlans = [
      ...landlordToPayPlans.filter(x => x.isRunScreeningAccepted === null), 
      ...renterToPayPlans.filter(x => x.isRunScreeningAccepted === null)
    ].map(x => x.orderPlanId);

    if(offer?.id && notAcceptedPlans?.length) {
      await acceptBackgroundCheckRequest({ applicationId: offer.id, orderPlanIds: notAcceptedPlans });
    } 

    handleClosePayScreeningServicePlanDialog();
  }

  const handleClosePayScreeningServicePlanDialog = () => {
    if(openPayScreeningServiceDialog) {
      setOpenPayScreeningServiceDialog(!openPayScreeningServiceDialog);
    }
    onClose();
  }
  
  useEffect(() => {
    if (!offer) return;

    const requestedPlans = offer.screeningConsents || [];
    const renterToPayPlans = requestedPlans.filter(x => !x.isPaymentConfirmed && x.screeningCheckStatus === ScreeningCheckStatus.RequestedRenter && x.isRunScreeningAccepted !== false);
    const landlordToPayPlans = requestedPlans.filter(x => x.screeningCheckStatus === ScreeningCheckStatus.RequestedLandlord);
    
    setAlreadyRequestedPlans(requestedPlans);
    setRenterToPayPlans(renterToPayPlans);
    setLandlordToPayPlans(landlordToPayPlans);

    if (renterToPayPlans?.length)  {
      setTotalSumForRenter(getFixedPlanPrice([renterToPayPlans[0]]));
    }
    setTotalSumForLandlord(getFixedPlanPrice(landlordToPayPlans.filter(x => x.isRunScreeningAccepted === null)));
  }, [offer]);

  const getFixedPlanPrice = (plans: ScreeningConsent[]) => {
    return Number(parseFloat(
      plans.reduce((sum, plan) => sum + (plan.price * (offer?.adultOccupants || 1)), 0).toFixed(2)
    ));
  }

  useEffect(() => {
    if (renterToPayPlans?.length) {
      const renterToPayPlan = renterToPayPlans[0];
      requestBgCheckPayment({
        orderPlanId: renterToPayPlan.orderPlanId,
        amount: renterToPayPlan.price,
        tenantId: offer?.renterId ?? '',
        rentApplicationId: offer?.id ?? '',
        requestorId: offer?.landlordId ?? ''
      });
    }
  }, [renterToPayPlans]);

  const onAccept = async () => {
    if (!offer) return;
    if (renterToPayPlans?.length) {
      setOpenPayScreeningServiceDialog(true);
    } else {
      await acceptBackgroundCheckRequest({ 
        applicationId: offer.id, 
        orderPlanIds: [...landlordToPayPlans.filter(x => x.isRunScreeningAccepted === null), ...renterToPayPlans.filter(x => x.isRunScreeningAccepted === null)].map(x => x.orderPlanId) });
    }
    closeModal();
  };

  const onDecline = async () => {
    if (!offer) return;
    const notDeclinedPlans = alreadyRequestedPlans.filter(x => 
      x.isRunScreeningAccepted === null || (x.screeningCheckStatus === ScreeningCheckStatus.RequestedRenter && !x.isPaymentConfirmed));
    notDeclinedPlans?.length && await declineBackgroundCheckRequest({ applicationId: offer.id, orderPlanIds: notDeclinedPlans.map(x => x.orderPlanId) });
    closeModal();
  };

  const closeModal = () => {
    setAcceptTermsAndCondition(false);
    onClose();
  }

  const handleTermsAndConditionChange = () => setAcceptTermsAndCondition(!acceptTermsConditions);
  const getAcceptButtonTitle = () => {
    return renterToPayPlans?.length
      ? (offer?.isRunScreeningHandled ? `Pay ($${totalSumForRenter})` : `Accept Background Check and Pay ($${totalSumForRenter})`)
      : 'Accept Background Check';
  }

  const isAnyNonHandledOrUnpaidConsent = () => alreadyRequestedPlans?.some(x => x.isRunScreeningAccepted === null || !x.isPaymentConfirmed);

  return (
    <div>
      {openPayScreeningServiceDialog && <PayTenantBackgroundScreeningDialog 
        title="Pay for Background Check"
        isLoading={pendingBgCheckPayment}
        isOpen={openPayScreeningServiceDialog} 
        data={screeningPaymentContext}
        useCapture={!!renterToPayPlans?.length && offer?.isRunScreeningHandled}
        onSuccess={handleScreeningPaymentSucceeded}
        onClose={handleClosePayScreeningServicePlanDialog}        
      />}
      <Modal
        title={'Background check is requested!'}
        isOpen={isOpen}
        isFullScreen={isMobile}
        contentClassName={'w-[670px]'}
        onOpenChange={(open) => {
          if (open) return;
          closeModal();
        }}
      >
        <div className='flex flex-col gap-3 p-2'>
          <div>
            {offer && <PropertyInfo
              location={offer.location}
              price={offer.price}
              image={offer.thumbnailUrl}
              isMulti={offer.estateType === EstateTypeDto.MultiUnit}
              unitNumber={offer.unitNumber}
            />}
          </div>
          <div className='flex flex-col gap-2'>
            <p className='text-center text-[16px] py-3 text-neutral-800 font-bold'>Landlord of the property has requested the following background check:</p>
            <div>
              {alreadyRequestedPlans && alreadyRequestedPlans
                .map(plan => (
                <DocumentCheckbox key={plan.orderPlanId}
                  name={plan.title}
                  checked={true}
                  disabled={plan.isRunScreeningAccepted !== null}
                  label={plan.title}
                />
              ))}
            </div>
      
          </div>
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col'>
              {totalSumForLandlord > 0 && <p className='text-center text-[16px] py-3 text-neutral-800 font-bold'>Cost of the background check (${totalSumForLandlord}) is to be paid by listing landlord.</p>}
              {totalSumForRenter > 0 && <p className='text-center text-[16px] py-3 text-neutral-800 font-bold'>Cost of the background check (${totalSumForRenter}) is to be paid by prospective tenant.</p>}
            </div>
            <div className='text-[15px] flex flex-col gap-2 px-5'>
              <p>
                FOR TENANT. In order to retrieve your credit information, we'll need to verify your identity with TransUnion. You'll need to provide your full social security number and answer a few security questions. This report is a soft inquiry and will not affect your credit report.
              </p>
              <p>
                PLEASE READ THIS ENTIRE AGREEMENT BEFORE PROCEEDING. BY CLICKING ON THE "ACCEPT" BUTTON BELOW, YOU ACCEPT THE TERMS AND CONDITIONS AND YOU AUTHORIZE TO HAVE THE ABOVE AMOUNT WITHDRAWN FROM THE SPECIFIED PAYMENT ACCOUNT. IF YOU DO NOT AUTHORIZE THIS PAYMENT OR ACCEPT THESE TERMS, DO NOT CLICK "ACCEPT" BELOW.
              </p>
              <RadioButton name={'T&C'}
                label={<span>I have read and accept the <Link className='text-sky-300 hover:text-sky-400' target='_blank' to={'/terms-of-service'}> Terms and Conditions </Link></span>}
                value={'T&C'}
                checked={acceptTermsConditions}
                disabled={isAcceptingBackgroundCheckRequest}
                onChange={handleTermsAndConditionChange}
              />
            </div>
          </div>
          <Button label={getAcceptButtonTitle()} theme={'primary'} onClick={onAccept} isLoading={isAcceptingBackgroundCheckRequest} disabled={!acceptTermsConditions || isAcceptingBackgroundCheckRequest || isDecliningBackgroundCheckRequest || !isAnyNonHandledOrUnpaidConsent()} />
          <div className='text-[15px] flex flex-col gap-2 p-3'>
            <p>
            Please be reminded that, as part of the pre-application consent, you have agreed to demonstrate the ability to pass a background screening. Landlord will likely not accept the offer if the  background check is declined.
            </p>
          </div>
          <Button label={'Decline Background Check'} theme={'gray'} className='text-black' onClick={onDecline} isLoading={isDecliningBackgroundCheckRequest} disabled={isAcceptingBackgroundCheckRequest || isDecliningBackgroundCheckRequest || !isAnyNonHandledOrUnpaidConsent()} />
          <Button label={'Close'} theme={'primary-outline'} onClick={closeModal} />
        </div>
      </Modal>
    </div>
  )
}

export default BackgroundCheckRequestModal